import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Chip,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Paper,
  alpha,
  Collapse,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Save,
  Close,
  ContentCopy,
  ExpandMore,
  ExpandLess,
  Check,
} from '@mui/icons-material';

/**
 * SpecsEditorDialog - Componente reutilizable para editar especificaciones de productos
 * 
 * Props:
 * - open: boolean - controla si el diálogo está abierto
 * - onClose: function - callback al cerrar
 * - product: object - producto con specs a editar
 * - onSave: function(specs) - callback al guardar specs
 * - saving: boolean - indica si se está guardando
 * - catalogSpecs: object - specs del catálogo (opcional, para override)
 */
const SpecsEditorDialog = ({ 
  open, 
  onClose, 
  product, 
  onSave, 
  saving = false,
  catalogSpecs = null 
}) => {
  const [specs, setSpecs] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSpec, setNewSpec] = useState({ key: '', value: '', type: 'string' });
  const [showCatalogSpecs, setShowCatalogSpecs] = useState(false);
  const [error, setError] = useState('');

  // Initialize specs when product changes
  useEffect(() => {
    if (product) {
      setSpecs(product.specs || {});
      setEditingKey(null);
      setShowAddForm(false);
      setNewSpec({ key: '', value: '', type: 'string' });
      setError('');
    }
  }, [product]);

  // Get catalog specifications
  const getCatalogSpecs = () => {
    if (catalogSpecs) return catalogSpecs;
    return product?.catalogData?.specifications || {};
  };

  const hasCatalogSpecs = Object.keys(getCatalogSpecs()).length > 0;

  // Handle edit start
  const handleEditStart = (key) => {
    setEditingKey(key);
    setEditValue(formatValueForEdit(specs[key]));
  };

  // Handle edit save
  const handleEditSave = () => {
    if (editingKey) {
      const parsedValue = parseValue(editValue, typeof specs[editingKey]);
      setSpecs(prev => ({
        ...prev,
        [editingKey]: parsedValue
      }));
      setEditingKey(null);
      setEditValue('');
    }
  };

  // Handle edit cancel
  const handleEditCancel = () => {
    setEditingKey(null);
    setEditValue('');
  };

  // Handle delete spec
  const handleDelete = (key) => {
    setSpecs(prev => {
      const newSpecs = { ...prev };
      delete newSpecs[key];
      return newSpecs;
    });
  };

  // Handle add new spec
  const handleAddSpec = () => {
    if (!newSpec.key.trim()) {
      setError('El nombre del campo es requerido');
      return;
    }

    const key = newSpec.key.trim().toLowerCase().replace(/\s+/g, '_');
    
    if (specs[key] !== undefined) {
      setError('Ya existe un campo con ese nombre');
      return;
    }

    const parsedValue = parseValue(newSpec.value, newSpec.type);
    
    setSpecs(prev => ({
      ...prev,
      [key]: parsedValue
    }));

    setNewSpec({ key: '', value: '', type: 'string' });
    setShowAddForm(false);
    setError('');
  };

  // Copy spec from catalog
  const handleCopyFromCatalog = (key, value) => {
    setSpecs(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Copy all specs from catalog
  const handleCopyAllFromCatalog = () => {
    const catalogSpecsObj = getCatalogSpecs();
    setSpecs(prev => ({
      ...prev,
      ...catalogSpecsObj
    }));
  };

  // Handle save
  const handleSave = () => {
    onSave(specs);
  };

  // Format value for display
  const formatValueForDisplay = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Sí' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Format value for editing
  const formatValueForEdit = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Parse value based on type
  const parseValue = (value, type) => {
    if (type === 'boolean' || type === 'boolean') {
      return value === 'true' || value === true;
    }
    if (type === 'number') {
      const num = parseFloat(value);
      return isNaN(num) ? value : num;
    }
    if (type === 'object') {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }
    return value;
  };

  // Format key for display
  const formatKeyForDisplay = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const specEntries = Object.entries(specs);
  const catalogSpecEntries = Object.entries(getCatalogSpecs());

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <Edit color="primary" />
        Editar Especificaciones
        {product && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            - {product.title || product.sku}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Custom Specs Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Especificaciones Personalizadas
            </Typography>
            <Button
              size="small"
              startIcon={showAddForm ? <Close /> : <Add />}
              onClick={() => setShowAddForm(!showAddForm)}
              variant={showAddForm ? 'outlined' : 'contained'}
            >
              {showAddForm ? 'Cancelar' : 'Agregar'}
            </Button>
          </Box>

          {/* Add New Spec Form */}
          <Collapse in={showAddForm}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02) }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Nueva Especificación
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Nombre del campo"
                  size="small"
                  value={newSpec.key}
                  onChange={(e) => setNewSpec(prev => ({ ...prev, key: e.target.value }))}
                  sx={{ flex: 1, minWidth: 150 }}
                  placeholder="ej: velocidad_impresion"
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={newSpec.type}
                    label="Tipo"
                    onChange={(e) => setNewSpec(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <MenuItem value="string">Texto</MenuItem>
                    <MenuItem value="number">Número</MenuItem>
                    <MenuItem value="boolean">Sí/No</MenuItem>
                  </Select>
                </FormControl>
                {newSpec.type === 'boolean' ? (
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Valor</InputLabel>
                    <Select
                      value={newSpec.value}
                      label="Valor"
                      onChange={(e) => setNewSpec(prev => ({ ...prev, value: e.target.value }))}
                    >
                      <MenuItem value="true">Sí</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    label="Valor"
                    size="small"
                    value={newSpec.value}
                    onChange={(e) => setNewSpec(prev => ({ ...prev, value: e.target.value }))}
                    sx={{ flex: 2, minWidth: 150 }}
                    type={newSpec.type === 'number' ? 'number' : 'text'}
                  />
                )}
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddSpec}
                  size="small"
                >
                  Agregar
                </Button>
              </Box>
            </Paper>
          </Collapse>

          {/* Specs List */}
          {specEntries.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {specEntries.map(([key, value]) => (
                <Paper
                  key={key}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:hover': {
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02)
                    }
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">
                      {key}
                    </Typography>
                    {editingKey === key ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <TextField
                          size="small"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          autoFocus
                          fullWidth
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') handleEditSave();
                            if (e.key === 'Escape') handleEditCancel();
                          }}
                        />
                        <IconButton size="small" onClick={handleEditSave} color="primary">
                          <Check fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={handleEditCancel}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography variant="body2" fontWeight={500}>
                        {formatValueForDisplay(value)}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={typeof value}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 10 }}
                  />
                  {editingKey !== key && (
                    <>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleEditStart(key)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" onClick={() => handleDelete(key)} color="error">
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Paper>
              ))}
            </Box>
          ) : (
            <Alert severity="info" sx={{ mt: 1 }}>
              No hay especificaciones personalizadas. Agrega una nueva o importa desde el catálogo.
            </Alert>
          )}
        </Box>

        {/* Catalog Specs Section */}
        {hasCatalogSpecs && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  py: 1,
                  '&:hover': { bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08) },
                  borderRadius: 1,
                  px: 1,
                  mx: -1,
                }}
                onClick={() => setShowCatalogSpecs(!showCatalogSpecs)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Especificaciones del Catálogo
                  </Typography>
                  <Chip label={catalogSpecEntries.length} size="small" color="secondary" />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={<ContentCopy />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyAllFromCatalog();
                    }}
                    variant="outlined"
                  >
                    Copiar Todas
                  </Button>
                  {showCatalogSpecs ? <ExpandLess /> : <ExpandMore />}
                </Box>
              </Box>

              <Collapse in={showCatalogSpecs}>
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {catalogSpecEntries.map(([key, value]) => {
                    const isOverridden = specs[key] !== undefined;
                    return (
                      <Paper
                        key={key}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.02),
                          opacity: isOverridden ? 0.6 : 1,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary">
                            {key}
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {formatValueForDisplay(value)}
                          </Typography>
                        </Box>
                        {isOverridden ? (
                          <Chip label="Ya copiada" size="small" color="success" variant="outlined" />
                        ) : (
                          <Tooltip title="Copiar a specs personalizadas">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyFromCatalog(key, value)}
                              color="primary"
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              </Collapse>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={<Save />}
        >
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpecsEditorDialog;
